import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const STATS = [
  { value: 92.0, suffix: "%", label: "Training Accuracy" },
  { value: 88.7, suffix: "%", label: "Validation Accuracy" },
  { value: 6.4, suffix: "%", prefix: "+", label: "Uplift with Agents" },
  { value: 640, suffix: "ms", label: "Inference Time" },
];

function Counter({ value, suffix = "", prefix = "", inView }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const duration = 1400;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setV(value * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);
  const display = value < 10 || value % 1 !== 0 ? v.toFixed(1) : Math.round(v).toString();
  return (
    <span className="font-mono-num text-5xl sm:text-6xl tracking-tight text-slate-900">
      {prefix}
      {display}
      <span className="text-slate-400 text-2xl sm:text-3xl ml-1">{suffix}</span>
    </span>
  );
}

export default function Performance() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="performance" ref={ref} className="py-24 md:py-32 bg-slate-900 text-slate-100 relative overflow-hidden">
      <div className="absolute inset-0 opacity-40">
        <div className="absolute -top-32 left-1/4 h-80 w-80 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="absolute -bottom-32 right-1/4 h-80 w-80 rounded-full bg-emerald-400/20 blur-3xl" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-sky-300/80">
            <span className="font-mono-num">06</span> · Performance
          </div>
          <h2 className="font-heading mt-4 text-4xl sm:text-5xl tracking-tight font-light text-white">
            Numbers that <em className="not-italic text-sky-300">hold up</em> <br />
            in the clinic.
          </h2>
          <p className="mt-5 text-slate-300 max-w-xl">
            Benchmarked against the ISIC-2019 reference set with a 7-class
            classification head.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="border-l border-slate-700 pl-5"
              data-testid={`stat-${i}`}
            >
              <Counter value={s.value} suffix={s.suffix} prefix={s.prefix || ""} inView={inView} />
              <div className="mt-3 text-[10px] uppercase tracking-[0.3em] font-mono-num text-slate-400">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
